import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface MultiSelectDropdownProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder?: string;
  loading?: boolean;
}

/**
 * Dropdown multi-select: click to open, tick several, and close by clicking
 * outside or the Done button. (A native <details> can't close on outside click,
 * which left the menus stuck open covering the form.)
 */
export function MultiSelectDropdown({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  loading = false,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (t: string) =>
    onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);

  const label = value.length === 0 ? placeholder : `${value.join(', ')} (${value.length})`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-1 flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
      >
        <span className={value.length === 0 ? 'text-muted-foreground' : ''}>{label}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-background shadow-md">
          <div className="max-h-56 overflow-auto p-1">
            {loading ? (
              <p className="px-2 py-1 text-sm text-muted-foreground">Loading…</p>
            ) : options.length === 0 ? (
              <p className="px-2 py-1 text-sm text-muted-foreground">No options</p>
            ) : (
              options.map((t) => (
                <label
                  key={t}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                >
                  <input type="checkbox" checked={value.includes(t)} onChange={() => toggle(t)} />
                  <span>{t}</span>
                </label>
              ))
            )}
          </div>
          <div className="border-t p-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full rounded px-2 py-1.5 text-sm font-medium text-primary hover:bg-accent"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
